/**
 * CompatibilityBoard — the Y-site compatibility dashboard.
 *
 * Presentational: the page (pages/Session.tsx) owns all data + agent state and
 * passes it down. The board owns only UI selection state and composes:
 *   - SubNav            (dataset + validity, source-outdated honesty)
 *   - MatrixToolbar     (search + ATC categories + legend / tone filter)
 *   - MatrixGrid        (the drug×drug grid)
 *   - DrugSidePanel     (additive pair view — 0, 1, or 2 DrugCards)
 *
 * Axis-aware slot model
 * ─────────────────────
 * A Y-site compatibility check is `matrix[i, j]` — one drug from the row
 * axis AND one from the column axis. The board owns one slot per axis:
 *   - `selectedRowDrugId` — last drug picked from the row axis (row header)
 *   - `selectedColDrugId` — last drug picked from the column axis (col header)
 *
 * Slot-update rules:
 *   row header click → replace `selectedRowDrugId` (or clear it if the same)
 *   col header click → replace `selectedColDrugId` (or clear it if the same)
 *   cell click       → atomic both-axis set (row = rowDrug, col = colDrug)
 *   card click in sidebar → clear whichever axis the card came from
 *
 * Two row-header clicks just rotate the row slot — they do NOT create a
 * pair. A pair only exists when BOTH axes have a drug. The verdict cell is
 * derived via `getCell(state, row.id, col.id)` (matrix is symmetric so
 * order doesn't matter for the lookup).
 *
 * Internal slot names (row/col) DON'T leak to the user — DrugSidePanel
 * receives `drugA` (= row) and `drugB` (= col); user-facing copy stays A/B.
 *
 * Sentinels
 * ─────────
 * Sentinels live ONLY on the column axis. They are stripped from rowDrugs
 * regardless of triangle mode, search, or category. Their blanket-rule
 * context is preserved by the sentinel COLUMNS, which appear as the
 * leftmost two cells of every drug row.
 *
 * Responsive layout — container queries, not viewport media
 * ─────────────────────────────────────────────────────────
 *   board container ≥ --two-column-min → in-flex side panel beside the matrix
 *   board container <  --two-column-min → matrix takes the full row; the panel
 *                                          opens as a right slide-over with
 *                                          backdrop dismiss + ESC + close X
 *
 * Movable sidebar: when the in-flex panel is shown, a drag handle on the
 * panel's left edge lets the user resize the width. Width persists per
 * browser via localStorage.
 *
 * Collapsible sidebar: the close X COLLAPSES the in-flex panel rather than
 * blanking it. A narrow expand-tab appears on the right of the matrix when
 * collapsed; tapping it reopens the sidebar with the last selection still
 * intact. Any new selection action (header/cell click) also auto-expands.
 * Collapsed-state persists per browser via localStorage. The overlay
 * variant is unaffected — its close X still clears + closes the Sheet.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './compat.css'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, X } from 'lucide-react'
import { MIN_TERM_LEN } from '@frontend/hooks/useMedicationSearch'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import type {
  CompatibilityBoardState,
  CompatibilityCell,
  CompatibilityDrug,
  VerdictTone,
} from '@frontend/components/ysite/types'
import { getCell } from '@frontend/components/ysite/types'
import { SubNav } from './SubNav'
import { MatrixToolbar } from './MatrixToolbar'
import { MatrixGrid } from './MatrixGrid'
import { CarriersGrid } from './CarriersGrid'
import { SeparateAdministrationList } from './SeparateAdministrationList'
import { DrugSidePanel, type PanelItem } from './DrugSidePanel'
import { PairPicker } from './PairPicker'
import { getCarrierDrugs, getNonCarrierDrugs } from './carriers'

// Token/surface CSS for this app (loads @frontend/design/tokens.css + the
// parchment/hairline/divider surface names the matrix uses).

export interface CompatibilityBoardProps {
  /** The institution matrix to render. */
  state: CompatibilityBoardState
}

/**
 * Default tone visibility — every tone shown. `as const` keeps it in
 * lockstep with `VerdictTone`: adding a tone to the union without listing
 * it here is a compile error.
 */
const ALL_TONES = ['ok', 'warn', 'bad', 'muted'] as const satisfies readonly VerdictTone[]

/** Persisted panel width key. Scope: per-browser, per-origin. */
const PANEL_WIDTH_LS_KEY = 'compat.panel.width'

/** Persisted panel collapsed key. Scope: per-browser, per-origin. */
const PANEL_COLLAPSED_LS_KEY = 'compat.panel.collapsed'

/**
 * Split a free-text search into individual drug-name tokens. Nurses type
 * pairs several ways — "fentanyl, ketamine", "fentanyl + ketamine",
 * "fentanyl and ketamine", "fentanyl/ketamine" (and the DE/FR conjunctions
 * "und"/"et"). We split on those separators, trim, and drop empties. Order
 * and count are preserved; the caller and the agent prompt decide what to do
 * with one, two, or more.
 */
const DRUG_QUERY_SEPARATORS = /\s*(?:,|\/|\+|&|\band\b|\bund\b|\bet\b)\s*/i

function parseDrugQuery(query: string): string[] {
  return query
    .split(DRUG_QUERY_SEPARATORS)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
}

/**
 * Generous clinical-name ceiling for a single searched token. Drug names sit
 * far under this; the bound exists only to stop an oversized paste from
 * becoming an absurd /medications query string, not to constrain real input.
 *
 * No character-stripping sanitise here any more: an off-matrix token now flows
 * ONLY to the medication API as an `encodeURIComponent`-escaped query param
 * (the SAI service uses parameterised SQL) and is shown verbatim as the card
 * title. It is NOT interpolated into any LLM prompt — the agent context fires
 * on matrix-controlled drug LABELS, never typed text — so stripping
 * punctuation would only desync the displayed title from what the nurse typed
 * (the old injection-defense rationale died with the SAI-card refactor).
 */
const MAX_DRUG_TOKEN_LEN = 80

/** The three tab views the board renders. */
type BoardTab = 'matrix' | 'carriers' | 'separate'

const BOARD_TABS: readonly BoardTab[] = ['matrix', 'carriers', 'separate']

function isBoardTab(value: string): value is BoardTab {
  return (BOARD_TABS as readonly string[]).includes(value)
}

/**
 * Tab state bound to `window.location.hash` so deep-links + reloads land
 * on the right tab. Default is `matrix` — the historical surface. Listens
 * for cross-tab navigation (hashchange) so the URL stays the source of
 * truth even if the user edits the bar.
 */
function useTabState(): [BoardTab, (next: BoardTab) => void] {
  const readHash = (): BoardTab => {
    if (typeof window === 'undefined') return 'matrix'
    const raw = window.location.hash.replace(/^#/, '')
    return isBoardTab(raw) ? raw : 'matrix'
  }
  const [tab, setTabState] = useState<BoardTab>(readHash)
  useEffect(() => {
    const onHash = () => setTabState(readHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const setTab = useCallback((next: BoardTab) => {
    if (typeof window !== 'undefined') {
      // Replace, not push — tab toggles shouldn't pollute back-button history.
      const url = `${window.location.pathname}${window.location.search}#${next}`
      window.history.replaceState(null, '', url)
    }
    setTabState(next)
  }, [])
  return [tab, setTab]
}

export function CompatibilityBoard({ state }: CompatibilityBoardProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  // Axis-aware selection — one slot per axis. A row-header click writes to
  // `selectedRowDrugId`; a col-header click writes to `selectedColDrugId`;
  // a cell click writes BOTH atomically.
  const [selectedRowDrugId, setSelectedRowDrugId] = useState<string | null>(
    null,
  )
  const [selectedColDrugId, setSelectedColDrugId] = useState<string | null>(
    null,
  )

  // Tab state — `matrix` (default, drug × drug Y-site), `carriers` (drug ×
  // carrier-solution table), `separate` (drugs flagged for own-line admin).
  // Persisted in `window.location.hash` so deep-links + reloads land on
  // the right tab. Selection state is intentionally NOT reset on tab
  // switch — the user can pick a drug on one tab and see it carry into
  // the next (e.g. select Fentanyl on the separate-admin list → switch
  // to carriers → row already highlighted).
  const [tab, setTab] = useTabState()

  // The board's flex row is the container-query container (see compat.css
  // `.compat-board-shell`). The CSS rule shows/hides the in-flex side panel
  // automatically when `inline-size >= --two-column-min`. React still needs
  // to know which mode we're in to decide whether selecting something opens
  // the overlay Sheet — so we watch the SAME element's width here and
  // compare against the SAME CSS custom property.
  const shellRef = useRef<HTMLDivElement>(null)
  const [fitsTwoColumns, setFitsTwoColumns] = useState(false)
  const [shellWidth, setShellWidth] = useState(0)
  // True when the board is narrow enough that the detail overlay should rise
  // from the bottom (phone) rather than slide in from the right (iPad portrait).
  const [overlayFromBottom, setOverlayFromBottom] = useState(false)
  useEffect(() => {
    const el = shellRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const width = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize || '16',
      )
      const cs = getComputedStyle(el)
      const thresholdPx =
        parseFloat(cs.getPropertyValue('--two-column-min') || '0') * rootFontSize
      const bottomMaxPx =
        parseFloat(cs.getPropertyValue('--overlay-bottom-max') || '0') * rootFontSize
      setFitsTwoColumns(width >= thresholdPx)
      setOverlayFromBottom(width < bottomMaxPx)
      setShellWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Movable sidebar — user-resizable width with persistence.
  const [panelWidth, setPanelWidth] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = window.localStorage.getItem(PANEL_WIDTH_LS_KEY)
    if (!stored) return null
    const parsed = parseFloat(stored)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  })

  // Collapse-into-tab state. When true (and in the in-flex layout), the
  // sidebar is hidden and a vertical expand-tab takes its place at the
  // right edge of the board. Selection state is PRESERVED across collapse/
  // expand so reopening restores context.
  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(PANEL_COLLAPSED_LS_KEY) === 'true'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (panelCollapsed) {
      window.localStorage.setItem(PANEL_COLLAPSED_LS_KEY, 'true')
    } else {
      window.localStorage.removeItem(PANEL_COLLAPSED_LS_KEY)
    }
  }, [panelCollapsed])

  // Sentinels (`(all carriers)`, `(any other drug)`) are SYNTHETIC keys we
  // added during JSON extraction to compactly encode the source PDF's
  // prose-form blanket rules — they are NOT literal columns in the official
  // LL0034-V07 document. Surfacing them as labeled columns in the main
  // matrix view was misleading (stakeholders ask "is that in our doc?" and
  // the honest answer is "no"). They are hidden here at the source: both
  // colDrugs and rowDrugs filter them out. The data they encode still drives
  // the Separate-administration tab (via the `(any other drug)` blanket
  // verdict — see `separateAdministration.ts`), so the information isn't
  // lost — only the misleading column rendering is.
  //
  // ATC category filtering was dropped — the chip strip ate vertical space
  // without earning it (search by drug name is the primary filter ICU
  // nurses reach for). Reintroduce as a popover/dropdown if needed.
  const colDrugs = useMemo(
    () => state.drugs.filter((d) => !d.sentinel),
    [state.drugs],
  )

  // Tokens the nurse typed, each treated as a separate drug filter. Nurses
  // type pairs as "fentanyl, ketamin" / "fentanyl + ketamin" etc. — splitting
  // FIRST is what was missing: the old code matched the whole comma-joined
  // string against each label, so a real pair (both IN the matrix) matched
  // nothing and dead-ended into an off-matrix lookup. Tokens below the
  // medication-service minimum (1-char fragments mid-typing) are dropped so
  // they neither flood the grid nor fire a bogus lookup.
  const queryTokens = useMemo(() => {
    if (query.trim() === '') return []
    return parseDrugQuery(query)
      .map((token) => token.trim())
      .filter((token) => token.length >= MIN_TERM_LEN)
  }, [query])

  // Match each token against the matrix. Matched drugs (token order, deduped)
  // become the search-filtered row axis — the search MANIPULATES the matrix
  // instead of replacing it. Tokens that hit nothing in the matrix are the
  // only ones that escalate to an off-matrix Swiss-medication (SAI) lookup:
  // the matrix is the primary object, the lookup is its fallback.
  const matchedDrugs = useMemo(() => {
    if (queryTokens.length === 0) return colDrugs
    const matched: CompatibilityDrug[] = []
    const seen = new Set<string>()
    for (const token of queryTokens) {
      const t = token.toLowerCase()
      for (const d of colDrugs) {
        const hay = [d.label, d.atc, d.snomed].filter(Boolean).join(' ').toLowerCase()
        if (hay.includes(t) && !seen.has(d.id)) {
          matched.push(d)
          seen.add(d.id)
        }
      }
    }
    return matched
  }, [colDrugs, queryTokens])

  // Search empty → full grid; otherwise the grid narrows to the matched drugs.
  const rowDrugs = queryTokens.length === 0 ? colDrugs : matchedDrugs

  // Each typed token → one side of the panel. A token that matches a matrix
  // drug IS that drug (an "axis"); one the matrix doesn't carry is free text,
  // looked up verbatim. Typed order is preserved; a length cap stops a giant
  // paste becoming an absurd query string. Gated to the matrix tab — the only
  // tab with a search box. Search NEVER mutates the tapped selection: the two
  // are merged into `panelItems` below, so "search one, tap the other" (and
  // every other combination) just works, and a two-drug search can no longer
  // fabricate a one-sided Y-site verdict.
  const searchItems = useMemo<PanelItem[]>(() => {
    if (tab !== 'matrix' || queryTokens.length === 0) return []
    return queryTokens.map((token) => {
      const tl = token.toLowerCase()
      const matched = colDrugs.find((d) =>
        [d.label, d.atc, d.snomed].filter(Boolean).join(' ').toLowerCase().includes(tl),
      )
      return matched
        ? { kind: 'drug' as const, drug: matched }
        : { kind: 'name' as const, name: token.slice(0, MAX_DRUG_TOKEN_LEN) }
    })
  }, [tab, colDrugs, queryTokens])

  // Carriers tab axes — drug rows (non-sentinel, non-carrier) × carrier
  // columns (NaCl 0.9% / Glucose 5/10/20% / etc.). Memoed on state.drugs
  // so they don't re-derive on every re-render.
  const carrierDrugs = useMemo(() => getCarrierDrugs(state), [state])
  const nonCarrierDrugs = useMemo(() => getNonCarrierDrugs(state), [state])

  // Tone visibility — see MatrixToolbar/MatrixGrid for how it's consumed.
  const [visibleTones, setVisibleTones] = useState<Set<VerdictTone>>(
    () => new Set<VerdictTone>(ALL_TONES),
  )
  const toggleTone = useCallback((tone: VerdictTone) => {
    setVisibleTones((prev) => {
      const next = new Set(prev)
      if (next.has(tone)) next.delete(tone)
      else next.add(tone)
      return next
    })
  }, [])
  const showAllTones = useCallback(() => {
    setVisibleTones(new Set<VerdictTone>(ALL_TONES))
  }, [])
  const allTonesHidden = visibleTones.size === 0

  // Any selection action also auto-expands the collapsed sidebar — the user
  // clicked to ask about a drug, so the sidebar should reappear to answer.
  const expandIfCollapsed = useCallback(() => {
    setPanelCollapsed((prev) => (prev ? false : prev))
  }, [])

  // Cell click — atomic both-axis assignment. Row = a, col = b.
  function selectCell(a: CompatibilityDrug, b: CompatibilityDrug, _cell: CompatibilityCell) {
    setSelectedRowDrugId(a.id)
    setSelectedColDrugId(b.id)
    expandIfCollapsed()
  }

  // Row-header click — set/clear the row axis slot. Column axis untouched.
  // Clicking the already-selected row drug clears it.
  const selectRowDrug = useCallback(
    (drug: CompatibilityDrug) => {
      setSelectedRowDrugId((prev) => (prev === drug.id ? null : drug.id))
      expandIfCollapsed()
    },
    [expandIfCollapsed],
  )

  // Column-header click — set/clear the col axis slot. Row axis untouched.
  const selectColDrug = useCallback(
    (drug: CompatibilityDrug) => {
      setSelectedColDrugId((prev) => (prev === drug.id ? null : drug.id))
      expandIfCollapsed()
    },
    [expandIfCollapsed],
  )

  // Explicit axis setters for the PairPicker (phone Matrix surface). Unlike
  // selectRowDrug/selectColDrug — which TOGGLE for the grid's header clicks —
  // these set or clear a slot outright (the picker has its own "clear ×"), so
  // re-picking the same drug doesn't blank it.
  //
  // Only a PICK (drug != null) auto-expands a collapsed panel — a CLEAR
  // (drug == null) must NOT re-open a panel the user explicitly collapsed.
  const setRowDrug = useCallback((drug: CompatibilityDrug | null) => {
    setSelectedRowDrugId(drug?.id ?? null)
    if (drug) expandIfCollapsed()
  }, [expandIfCollapsed])
  const setColDrug = useCallback((drug: CompatibilityDrug | null) => {
    setSelectedColDrugId(drug?.id ?? null)
    if (drug) expandIfCollapsed()
  }, [expandIfCollapsed])

  // Close X on the in-flex panel — COLLAPSES the panel (selection preserved
  // so re-expanding restores context).
  const collapsePanel = useCallback(() => {
    setPanelCollapsed(true)
  }, [])

  // Clear-everything — wired to the overlay's backdrop dismiss + ESC + the
  // Sheet's controlled `onOpenChange` AND the overlay's close X. Does NOT
  // touch panelCollapsed (the overlay path uses a different visibility
  // mechanism — the Sheet itself).
  const clearSelection = useCallback(() => {
    setSelectedRowDrugId(null)
    setSelectedColDrugId(null)
    // Also clear the search — in off-matrix mode the sidebar is driven by the
    // query, not by a selection, so dismissing the overlay must drop the
    // query too. Otherwise offMatrixSearch stays true and the overlay
    // immediately re-opens. Harmless in selection mode (query is already '').
    setQuery('')
  }, [])

  const selectedRowDrug = useMemo(
    () => state.drugs.find((d) => d.id === selectedRowDrugId) ?? null,
    [state.drugs, selectedRowDrugId],
  )
  const selectedColDrug = useMemo(
    () => state.drugs.find((d) => d.id === selectedColDrugId) ?? null,
    [state.drugs, selectedColDrugId],
  )

  // The panel's sides — the ONE place search and tap meet. Tapped drugs
  // (explicit) come first, then searched tokens in typed order; deduped and
  // capped at two. This is what makes every combination work: search both,
  // tap both, or search one and tap the other. Sentinels carry no SAI record,
  // so they never become a card.
  const panelItems = useMemo<PanelItem[]>(() => {
    const out: PanelItem[] = []
    const seen = new Set<string>()
    const pushDrug = (d: CompatibilityDrug | null) => {
      if (!d || d.sentinel || out.length >= 2) return
      const k = `d:${d.id}`
      if (!seen.has(k)) {
        out.push({ kind: 'drug', drug: d })
        seen.add(k)
      }
    }
    const pushName = (n: string) => {
      if (out.length >= 2) return
      const k = `n:${n.toLowerCase()}`
      if (!seen.has(k)) {
        out.push({ kind: 'name', name: n })
        seen.add(k)
      }
    }
    pushDrug(selectedRowDrug)
    pushDrug(selectedColDrug)
    for (const it of searchItems) {
      if (out.length >= 2) break
      if (it.kind === 'drug') pushDrug(it.drug)
      else pushName(it.name)
    }
    return out
  }, [selectedRowDrug, selectedColDrug, searchItems])

  // The two matrix drugs in play (in panel order), and their Y-site verdict.
  // A verdict exists ONLY for two real matrix drugs the matrix scores —
  // `getCell` returns null for self-pairs and pairs absent from the matrix.
  // Off-matrix names never reach here, so search can't conjure a verdict.
  const panelDrugIds = useMemo(
    () =>
      panelItems
        .filter((i): i is { kind: 'drug'; drug: CompatibilityDrug } => i.kind === 'drug')
        .map((i) => i.drug.id),
    [panelItems],
  )
  const panelCell = useMemo<CompatibilityCell | null>(() => {
    if (panelDrugIds.length !== 2) return null
    return getCell(state, panelDrugIds[0], panelDrugIds[1])
  }, [state, panelDrugIds])

  // Matrix highlight reflects the panel's sides: two drugs → highlight their
  // pair cell; one drug → highlight its row/column line. Driven by panelItems
  // so a SEARCHED drug highlights in the grid exactly like a tapped one.
  const pairTuple: [string, string] | null =
    panelDrugIds.length === 2 ? [panelDrugIds[0], panelDrugIds[1]] : null
  const singleHighlightId = !pairTuple ? (panelDrugIds[0] ?? null) : null

  // The sidebar is worth showing whenever a side is filled — searched or tapped.
  const sidebarActive = panelItems.length > 0
  // Overlay opens whenever the sidebar is active AND the in-flex layout
  // can't fit. `panelCollapsed` is intentionally NOT a factor here — the
  // overlay variant uses its own visibility (the Sheet).
  //
  // EXCEPTION — phone Matrix tab: there the PairPicker IS the surface and
  // renders the verdict + medication cards inline on one scrollable column.
  // Letting the detail overlay ALSO auto-open would hijack the screen after
  // the first pick, and dismissing it clears the pair (its "I'm done"
  // semantics) — so a two-drug verdict could never be reached. We suppress
  // the overlay on that one surface; carriers/separate on phone still use it.
  const phoneMatrixSurface = tab === 'matrix' && overlayFromBottom
  const overlayOpen = !fitsTwoColumns && sidebarActive && !phoneMatrixSurface
  // Expand-tab visibility — only in the in-flex layout AND collapsed.
  // The expand tab and the sidebar are mutually exclusive on the same edge.
  const expandTabVisible = fitsTwoColumns && panelCollapsed

  // Auto-expand the collapsed sidebar whenever a side fills (search or tap) —
  // the user asked about a drug, so the panel that answers must reappear.
  useEffect(() => {
    if (panelItems.length > 0) expandIfCollapsed()
  }, [panelItems, expandIfCollapsed])

  // Movable-sidebar resize logic. Pointer events handle mouse + touch in
  // one API; pointer-capture keeps the drag alive even if the pointer
  // leaves the handle.
  const dragRef = useRef<{
    startX: number
    startWidth: number
    minPx: number
    maxPx: number
  } | null>(null)

  const onResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const shell = shellRef.current
      if (!shell) return
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize || '16',
      )
      const minPx = 18 * rootFontSize
      const maxPx = Math.max(minPx, shell.clientWidth * 0.5)
      const aside = shell.querySelector<HTMLElement>('.compat-side-panel')
      const startWidth = panelWidth ?? aside?.getBoundingClientRect().width ?? minPx
      dragRef.current = { startX: e.clientX, startWidth, minPx, maxPx }
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      e.preventDefault()
    },
    [panelWidth],
  )

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const next = Math.min(drag.maxPx, Math.max(drag.minPx, drag.startWidth - dx))
    setPanelWidth(next)
  }, [])

  const onResizeEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return
      dragRef.current = null
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      if (panelWidth !== null && typeof window !== 'undefined') {
        window.localStorage.setItem(PANEL_WIDTH_LS_KEY, String(Math.round(panelWidth)))
      }
    },
    [panelWidth],
  )

  // Clamp persisted width to current bounds if the viewport shrinks.
  useEffect(() => {
    if (panelWidth === null || shellWidth === 0) return
    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize || '16',
    )
    const minPx = 18 * rootFontSize
    const maxPx = Math.max(minPx, shellWidth * 0.5)
    if (panelWidth < minPx || panelWidth > maxPx) {
      setPanelWidth(Math.min(maxPx, Math.max(minPx, panelWidth)))
    }
  }, [panelWidth, shellWidth])

  // Inline panel style. When collapsed, force `display: none` — this
  // beats the `@container` rule's `display: block` in the cascade because
  // element-level inline styles win.
  const panelStyle: React.CSSProperties = {
    ...(panelWidth !== null ? { width: `${panelWidth}px` } : {}),
    ...(panelCollapsed ? { display: 'none' } : {}),
  }

  return (
    <div ref={shellRef} className="compat-board-shell flex h-full flex-col bg-[var(--parchment)]">
      <SubNav state={state} pairCount={Object.keys(state.cells).length} />

      <div className="flex min-h-0 flex-1">
        {/* Left column: toolbar + matrix. min-w-0 lets it shrink so the
            matrix's overflow-auto kicks in instead of pushing the panel
            off-screen. */}
        {/* Left column = flex column with NO scroll itself. Scroll lives
            on the matrix below, so the matrix's `<th sticky top-0>` and
            `<th sticky left-0>` cells stick to the matrix's own viewport
            on BOTH axes (column headers freeze on vertical scroll, row
            headers freeze on horizontal scroll — Excel-style freeze panes).
            Putting the scroll on an OUTER element would mean vertical
            scroll moves the whole matrix away and the sticky `<thead>`
            sticks only to the wrong scroll context. The `min-h-0 flex-1`
            chain is what lets the matrix shrink to the remaining vertical
            space instead of pushing the column past the viewport. */}
        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* min-w-0 on every wrapper from here down — without it, flex
              children default to `min-width: auto` (content-sized) and the
              wide matrix table pushes the column past the viewport,
              disabling the matrix's own overflow-auto. The constraint chain
              must reach the matrix's overflow container unbroken. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-3 sm:p-4 lg:p-5">
            {/* Tab strip — three views over the same matrix data:
                  matrix    drug × drug Y-site (original surface)
                  carriers  drug × carrier-solution (NaCl/Glucose/Ringer/…)
                  separate  drugs that must be administered through their own line
                URL-hash bound so deep-links land on the right tab. */}
            <BoardTabStrip tab={tab} onChange={setTab} />

            {tab === 'matrix' && (
              <>
                {/* Narrow CONTAINER (board below the 48rem knob in
                    compat.css — a phone, or a pane on any desktop): the N×N
                    grid is illegible and pan-bound, so we swap in PairPicker —
                    a touch-native A/B picker that yields the verdict directly.
                    It writes the SAME axis slots the grid uses, so the side
                    panel + verdict derivation light up unchanged. */}
                <div className="compat-pair-surface min-h-0 min-w-0 flex-1 flex-col">
                  <PairPicker
                    state={state}
                    colDrugs={colDrugs}
                    drugA={selectedRowDrug}
                    drugB={selectedColDrug}
                    onSelectA={setRowDrug}
                    onSelectB={setColDrug}
                  />
                </div>

                {/* Wide CONTAINER (board at/above the 48rem knob): the full
                    grid, where the triangle matrix is legible and pointer
                    hover earns its tooltip. The swap is the board's own
                    container, never the viewport — see compat.css. */}
                <div className="compat-matrix-surface min-h-0 min-w-0 flex-1 flex-col gap-3">
                  <MatrixToolbar
                    query={query}
                    onQuery={setQuery}
                    visibleTones={visibleTones}
                    onToggleTone={toggleTone}
                    onShowAllTones={showAllTones}
                  />

                  {/* Matrix section fills remaining vertical space so the
                      matrix's own overflow-auto becomes the BOTH-AXIS scroll
                      container (vertical AND horizontal). That's what makes
                      the sticky `<thead>` and sticky leftmost `<th>` freeze
                      on the correct axes — Excel-style freeze panes. */}
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    {/* Section h2 ("Compatibility matrix" + count) removed
                        to reclaim vertical real estate — the SubNav above
                        already shows "Dataset USB LL0034-V07 · 1049 pairs",
                        and the tab strip says "Matrix". A third title
                        restating the same thing was a row of gilding. */}
                    {allTonesHidden && (
                      <div className="mb-3 rounded-[12px] border border-[var(--hairline)] bg-[var(--canvas)] px-4 py-3 text-[13px] text-neutral-600">
                        {t('compatibility.toneFilter.allHidden')}
                      </div>
                    )}
                    <MatrixGrid
                      state={state}
                      query={query}
                      rowDrugs={rowDrugs}
                      colDrugs={colDrugs}
                      visibleTones={visibleTones}
                      // Triangle when search is empty — at that point rowDrugs
                      // is exactly colDrugs minus sentinels, i.e. the symmetric
                      // drug × drug subgrid where every pair appears twice.
                      // Category narrowing keeps symmetry (it scopes both axes
                      // the same way) so it doesn't break the triangle.
                      triangle={query.trim() === ''}
                      selectedDrugId={singleHighlightId}
                      selectedPair={pairTuple}
                      onSelectCell={selectCell}
                      onSelectRowDrug={selectRowDrug}
                      onSelectColDrug={selectColDrug}
                    />
                  </div>
                </div>
              </>
            )}

            {tab === 'carriers' && (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {/* Section header dropped for the same reason as Matrix —
                    the tab strip already says "Carriers"; restating it
                    eats a row. The grid itself is the answer. */}
                <CarriersGrid
                  state={state}
                  drugDrugs={nonCarrierDrugs}
                  carrierDrugs={carrierDrugs}
                  visibleTones={visibleTones}
                  selectedDrugId={singleHighlightId}
                  selectedPair={pairTuple}
                  // Reuse the same axis slots: drug → row, carrier → col.
                  // selectRowDrug/selectColDrug write to the same state
                  // (selectedRowDrugId/selectedColDrugId) the matrix uses,
                  // so DrugSidePanel + auto-fire light up unchanged.
                  onSelectCell={selectCell}
                  onSelectDrug={selectRowDrug}
                  onSelectCarrier={selectColDrug}
                />
              </div>
            )}

            {tab === 'separate' && (
              <SeparateAdministrationList
                state={state}
                selectedDrugId={selectedRowDrugId}
                onSelectDrug={selectRowDrug}
              />
            )}
          </div>
      </div>

        {/* In-flex side panel. Visibility is controlled by the container
            query on .compat-side-panel in compat.css AND by the React
            `panelCollapsed` flag (inline `display: none` overrides the
            @container rule when collapsed). */}
        <aside
          className="compat-side-panel relative border-l border-[var(--divider)] bg-[var(--canvas)]"
          style={panelStyle}
        >
          {/* Resize handle on the panel's LEFT edge. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t('compatibility.panel.resizeHandle')}
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            className="compat-resize-handle"
          />
          {/* Close X here COLLAPSES the panel (preserves selection). */}
          <PanelCloseButton onClose={collapsePanel} visible={sidebarActive} t={t} />
          <ScrollArea className="h-full">
            <DrugSidePanel items={panelItems} cell={panelCell} />
          </ScrollArea>
        </aside>

        {/* Expand tab — narrow vertical strip at the right edge of the
            board when the sidebar is collapsed. Only rendered in the
            in-flex layout (overlay variant has its own close semantics). */}
        {expandTabVisible && (
          <PanelExpandTab onExpand={() => setPanelCollapsed(false)} t={t} />
        )}
      </div>

      {/* Overlay variant: a slide-over with backdrop dismiss, ESC, and an
          in-panel close X. Used when the in-flex panel can't fit. On a phone
          (narrow board) it rises from the BOTTOM — one-handed reach; on iPad
          portrait it slides in from the RIGHT. The close X here CLEARS the
          selection (different semantics from the in-flex collapse — the
          overlay closing implies "I'm done"). */}
      <Sheet
        open={overlayOpen}
        onOpenChange={(open) => {
          if (!open) clearSelection()
        }}
      >
        <SheetContent
          side={overlayFromBottom ? 'bottom' : 'right'}
          // `data-variant` is the typed source of truth the compat.css width
          // rules target — never the class string (a stray `rounded-t-*`
          // utility on the right variant would silently flip a class-sniffing
          // selector).
          data-variant={overlayFromBottom ? 'bottom' : 'right'}
          // NO `relative` here. SheetContent's base variant is `position: fixed`
          // (it slides over, portaled to <body>); a `relative` in this className
          // would be a same-group position utility that tailwind-merge resolves
          // LAST-wins, silently demoting the panel to `position: relative`. A
          // portaled relative element falls to the END of <body> in normal flow —
          // below the fold — so the verdict renders off screen while the fixed
          // backdrop still dims the board. The panel's `absolute` close button gets its
          // containing block from `fixed` just the same, so nothing needs it.
          className={cn(
            'compat-overlay-panel bg-[var(--canvas)] p-0 shadow-none [&>button]:hidden',
            overlayFromBottom
              ? 'h-[85dvh] rounded-t-[18px] border-t border-[var(--divider)] pb-[env(safe-area-inset-bottom)]'
              : 'border-l border-[var(--divider)] sm:max-w-none',
          )}
        >
          <SheetTitle className="sr-only">
            {t('compatibility.panel.overlayTitle')}
          </SheetTitle>
          <PanelCloseButton onClose={clearSelection} visible={sidebarActive} t={t} />
          <ScrollArea className="h-full">
            <DrugSidePanel items={panelItems} cell={panelCell} />
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default CompatibilityBoard

/**
 * Three-pill tab strip. Sits inside the left column, above the per-tab
 * content. Visual language matches the existing MatrixToolbar category
 * pills: rounded-full, hairline border, primary fill when active. No
 * shadcn Tabs — a custom strip keeps the bundle small and the styling
 * 1:1 with the rest of the board.
 *
 * Translation keys carry safe defaults so a missing locale string never
 * blanks the tab label at the bedside.
 */
function BoardTabStrip({
  tab,
  onChange,
}: {
  tab: BoardTab
  onChange: (next: BoardTab) => void
}) {
  const { t } = useTranslation()
  const items: { id: BoardTab; label: string }[] = [
    { id: 'matrix', label: t('compatibility.tabs.matrix', 'Matrix') },
    { id: 'carriers', label: t('compatibility.tabs.carriers', 'Carriers') },
    {
      id: 'separate',
      label: t('compatibility.tabs.separate', 'Separate administration'),
    },
  ]
  return (
    <div
      role="tablist"
      aria-label={t('compatibility.tabs.aria', 'Compatibility views')}
      className="flex flex-wrap items-center gap-1.5"
    >
      {items.map(({ id, label }) => {
        const active = id === tab
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            className={cn(
              'rounded-full border px-4 py-1.5 text-[13px] font-semibold transition active:scale-95',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-focus',
              active
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-[var(--hairline)] bg-[var(--canvas)] text-neutral-700 hover:border-primary-500 hover:text-primary-600',
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Shell-level close button — sibling of the panel's ScrollArea (not a
 * descendant), so it stays pinned regardless of scroll. 44×44 hit area
 * meets the HIG touch floor.
 *
 * Semantics depend on the caller: in the in-flex layout `onClose`
 * collapses the panel (selection preserved); in the overlay it clears
 * the selection (closing the overlay implies "I'm done").
 */
function PanelCloseButton({
  onClose,
  visible,
  t,
}: {
  onClose: () => void
  visible: boolean
  t: (key: string) => string
}) {
  if (!visible) return null
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('compatibility.panel.close')}
      className={cn(
        'absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full',
        'bg-[var(--canvas)] text-neutral-500 transition active:scale-95',
        'border border-[var(--hairline)]',
        'hover:bg-neutral-100 hover:text-neutral-900',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-focus',
      )}
    >
      <X className="h-5 w-5" aria-hidden />
    </button>
  )
}

/**
 * Expand tab — narrow vertical strip at the right edge of the board that
 * replaces the sidebar when collapsed. Full board height so any tap
 * inside counts (the strip's 32px width is below the HIG 44px floor on
 * its own, but the full-height span compensates). Chevron-left icon hints
 * "this opens leftward". Visibility is also gated by the container query
 * on .compat-expand-tab (compat.css) so it never shows in the overlay
 * layout even if mistakenly rendered.
 */
function PanelExpandTab({
  onExpand,
  t,
}: {
  onExpand: () => void
  t: (key: string) => string
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={t('compatibility.panel.expand')}
      className="compat-expand-tab"
    >
      <ChevronLeft className="h-5 w-5" aria-hidden />
    </button>
  )
}
